import { useCallback, useContext, useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { VisibilityContext } from 'react-horizontal-scrolling-menu';

// react-horizontal-scrolling-menu's scrollPrev/scrollNext and
// isFirstItemVisible/isLastItemVisible are derived from an IntersectionObserver
// that (re)populates asynchronously after mount and after any layout shift
// (e.g. late-loading ad iframes). That makes them racy — clicking before the
// observer has reported in silently no-ops. `scrollContainer` is the actual
// scrollable element, set via a plain ref during React's commit phase, so
// scrolling and edge state from it are synchronous and reliable instead.
function useEdgeState(scrollContainer: RefObject<HTMLElement | null>) {
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEdgeState = useCallback(() => {
    const el = scrollContainer.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, [scrollContainer]);

  useEffect(() => {
    const el = scrollContainer.current;
    if (!el) return;
    updateEdgeState();
    el.addEventListener('scroll', updateEdgeState, { passive: true });
    const resizeObserver = new ResizeObserver(updateEdgeState);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener('scroll', updateEdgeState);
      resizeObserver.disconnect();
    };
  }, [scrollContainer, updateEdgeState]);

  return { atStart, atEnd };
}

export function LeftArrow() {
  const { scrollContainer } = useContext(VisibilityContext);
  const { atStart } = useEdgeState(scrollContainer);

  return (
    <button
      className="scrollmenu-arrow-btn scrollmenu-left"
      disabled={atStart}
      onClick={() =>
        scrollContainer.current?.scrollBy({
          left: -scrollContainer.current.clientWidth,
          behavior: 'smooth',
        })
      }
    >
      <img src="/img/back.svg" alt="Scroll left" />
    </button>
  );
}

export function RightArrow() {
  const { scrollContainer } = useContext(VisibilityContext);
  const { atEnd } = useEdgeState(scrollContainer);

  return (
    <button
      className="scrollmenu-arrow-btn scrollmenu-right"
      disabled={atEnd}
      onClick={() =>
        scrollContainer.current?.scrollBy({
          left: scrollContainer.current.clientWidth,
          behavior: 'smooth',
        })
      }
    >
      <img src="/img/forward.svg" alt="Scroll right" />
    </button>
  );
}
